import React from 'react';
import T from 'Components/Helpers/translate';
import type Contact from 'App/resources/Model/Contact';
import { leadStatus as LeadStatus } from 'App/babel/components/LegacyReactTemplates';

type Props = {
	duplicates: Contact[];
	selectedDuplicate: Contact | null;
	connecting: boolean;
	currentClientId?: number;
	filteredListLink: string;
	getContactLink: (contact: Contact) => string;
	getCompanyLink: (contact: Contact) => string;
	onSelect: (duplicate: Contact) => void;
	onEdit: (contactId: number) => void;
	onConnect: (duplicate: Contact) => void;
};

const EmailDuplicateWarning = ({
	duplicates,
	selectedDuplicate,
	connecting,
	currentClientId,
	filteredListLink,
	getContactLink,
	getCompanyLink,
	onSelect,
	onEdit,
	onConnect
}: Props) => {
	const isAlreadyConnected = (duplicate: Contact) =>
		!!currentClientId && !!duplicate.client && duplicate.client.id === currentClientId;
	const showRadio = duplicates.length > 1;

	return (
		<div id="emailSuggestionWarning" className="email-suggestion-warning show-email-suggestion">
			<div className="section-row">
				<b>{T('contact.emailSuggestWarning')}</b>
				<b data-elevio-article="1005" className="fa fa-info-circle" />
			</div>

			{duplicates.length > 5 ? (
				<div className="section-row no-border no-padding-top">
					<a
						href={filteredListLink}
						target="_blank"
						rel="noreferrer"
						className="btn up-btn no-shadow btn-primary-action"
					>
						{T('contact.viewContactsWithSuchEmail')}
					</a>
				</div>
			) : (
				duplicates.map(duplicate => {
					const selected = !!selectedDuplicate && selectedDuplicate.id === duplicate.id;
					return (
						<div
							key={duplicate.id}
							className={
								'section-row contact-info' +
								(showRadio ? ' contact-info--selectable' : '') +
								(selected ? ' contact-info--selected' : '')
							}
							onClick={() => onSelect(duplicate)}
						>
							<div className="section-cell-holder">
								{showRadio ? (
									<div className="duplicate-radio">
										{selected ? <div className="duplicate-radio__dot" /> : null}
									</div>
								) : (
									<div className="section-cell">
										<i className="fa fa-user" />
									</div>
								)}
								<div className="section-cell">
									<a
										target="_blank"
										rel="noreferrer"
										className="contact-link"
										href={getContactLink(duplicate)}
										onClick={e => e.stopPropagation()}
									>
										{duplicate.name}
									</a>
									<div className="lead-status">
										<LeadStatus
											activity={duplicate.hasActivity}
											activityOld={duplicate.hadActivity}
											appointment={duplicate.hasAppointment}
											appointmentOld={duplicate.hadAppointment}
											opportunity={duplicate.hasOpportunity}
											opportunityOld={duplicate.hadOpportunity}
											order={duplicate.hasOrder}
											orderOld={duplicate.hadOrder}
										/>
										<div className="section-cell">
											<a
												target="_blank"
												rel="noreferrer"
												className="account-link"
												href={getCompanyLink(duplicate)}
												onClick={e => e.stopPropagation()}
											>
												{duplicate.client.name}
											</a>
											{isAlreadyConnected(duplicate) ? (
												<span className="badge-already-connected">
													{T('contact.duplicateConnect.alreadyConnected')}
												</span>
											) : null}
										</div>
									</div>
								</div>
							</div>
							<button
								type="button"
								className="edit-btn"
								onClick={e => {
									e.stopPropagation();
									onEdit(duplicate.id);
								}}
							>
								<i className="fa fa-pencil" />
							</button>
						</div>
					);
				})
			)}

			{selectedDuplicate && currentClientId ? (
				<div className="section-row no-border duplicate-connect-footer">
					{duplicates.length === 1 ? (
						<div style={{ paddingTop: 6 }}>
							<span>{T('contact.duplicateConnect.prompt')}</span>{' '}
							<span className="text-muted">{T('contact.duplicateConnect.recommended')}</span>
						</div>
					) : null}
					<button
						type="button"
						className="btn up-btn no-shadow btn-primary-action"
						disabled={connecting}
						onClick={() => onConnect(selectedDuplicate)}
					>
						{connecting ? (
							<span>
								{T('contact.duplicateConnect.connecting')} <i className="fa fa-refresh fa-spin" />
							</span>
						) : (
							<span>
								<i className="fa fa-link" /> {T('contact.duplicateConnect.button')}
							</span>
						)}
					</button>
				</div>
			) : null}
		</div>
	);
};

export default EmailDuplicateWarning;
